/**
 * Shared helpers for turning a charted feature into the two things the UI keeps
 * needing about it: a single representative [lon, lat] for its charted position,
 * and a short human title. Both the feature detail sheet and the adaptive
 * long-press ring read a charted object the same way, so they share this rather
 * than each carrying their own copy.
 */
import type { ChartFeature } from '@/types';
import type { Geometry } from 'geojson';
import { objClassLabel, isLighthouse } from '@/chart';

/** A representative [lon, lat] for a feature's charted position. */
export function featurePoint(geom: Geometry | null | undefined): [number, number] | null {
  if (!geom) return null;
  if (geom.type === 'Point') return geom.coordinates as [number, number];
  if (geom.type === 'LineString') {
    const c = geom.coordinates;
    return c.length ? (c[Math.floor(c.length / 2)] as [number, number]) : null;
  }
  if (geom.type === 'Polygon') {
    const ring = geom.coordinates[0];
    if (!ring?.length) return null;
    let sx = 0;
    let sy = 0;
    for (const pt of ring) {
      sx += pt[0];
      sy += pt[1];
    }
    return [sx / ring.length, sy / ring.length];
  }
  return null;
}

/**
 * A short title for a charted object: its charted name when it has one, else the
 * plain-English class label (a lighthouse landmark reads "Lighthouse" rather than
 * the generic landmark label, matching the detail sheet).
 */
export function featureTitle(feature: ChartFeature): string {
  const attrs = (feature.properties.attrs ?? {}) as Record<string, unknown>;
  const raw = (attrs.raw ?? {}) as Record<string, unknown>;
  const oc = feature.properties.objClass;
  const name = attrs.name as string | undefined;
  const lighthouse = oc === 'LNDMRK' && isLighthouse(raw.CATLMK);
  return name || (lighthouse ? 'Lighthouse' : objClassLabel(oc));
}
